import { Component, OnInit, Inject, Input, Output, EventEmitter } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgForm, ValidationErrors, UntypedFormControl } from '@angular/forms';
import { finalize, startWith, map } from 'rxjs/operators';

import { QuoteService } from '../../../quote/services/quote.service';
import { Quote } from '../../models/quote.interface';
import { Customer } from '../../models/customer.interface';
import { ProjectCustomerEdit } from '../../models/project.customer.edit.interface';
import { Office } from '../../models/office.interface';
import { User } from '../../models/user.interface';
import { LeadSourceType } from '../../models/lead.source.type.interface';
import { JobType } from '../../models/job.type.interface';
import { CustomerType } from '../../models/customer.type.interface';
import { ContactType } from '../../models/contact.type.interface';
import { PaymentType } from '../../models/payment.type.interface';
import { PaymentTerm } from '../../models/payment.term.interface';
import { Observable } from 'rxjs';
import { RolePermission } from '../../models/role.permission.enum';
import { DeviceDetectorService } from 'ngx-device-detector';
import { MatSnackBar } from '@angular/material/snack-bar';
import { QuoteAssignment } from '../../models/quote.assignment';

@Component({
  template: ''
})
export abstract class ProjectInfoComponent implements OnInit {
  project: Quote = {
    customer: {} as ProjectCustomerEdit,
  } as Quote;

  customerControl = new UntypedFormControl();

  errors: string;
  isRequesting: boolean;
  isMobile: boolean;

  projectTypeIn: string;
  quoteIn: Quote;
  customerIdIn: number;

  isRedirect: boolean;

  readOnly: boolean = false;

  customers: Customer[];
  customerTypes: CustomerType[];
  contactTypes: ContactType[];
  offices: Office[];
  states: string[];
  salesPersons: User[];
  jobTypes: JobType[];
  leadSourceTypes: LeadSourceType[];
  paymentTerms: PaymentTerm[];
  paymentTypes: PaymentType[];
  quoteTemplates: Quote[];

  filteredCustomers: Observable<Customer[]>;
  customer?: Customer;

  blueprintPlans: string = 'false';
  fieldMeasure: string = 'false';

  jobAddressCustom: boolean = false;

  constructor(private quoteService: QuoteService, private deviceDetectorService: DeviceDetectorService, private snackBar: MatSnackBar, private router: Router) {
    this.isMobile = !this.deviceDetectorService.isDesktop();
  }

  @Input()
  public set quote(value: Quote) {
    this.quoteIn = value;

    if (value && value.quote_ID) {
      // clone object to keep changes local
      let quote = (<any>Object).assign({}, value) as Quote;

      this.loadQuote(quote);
    }
    else {
      this.checkReadOnly();
    }
  }

  @Input()
  public set customerId(value: number) {
    this.customerIdIn = value;

    if (value) {
      this.project.customer_ID = value;

      this.refreshCustomer();
    }
  }

  @Input()
  public set projectType(value: string) {
    this.projectTypeIn = value;
    if (value == "lead") {
      this.project.quote_IsLead = true;
      this.readOnly = ((this.project.quote_ID !== 0 && this.project.quote_ID !== undefined) && !QuoteService.hasPermission(RolePermission.EditLead)) && ((this.project.quote_ID === 0 || this.project.quote_ID === null || this.project.quote_ID === undefined) && !QuoteService.hasPermission(RolePermission.AddLead));
    }
    if (value == "quote") {
      this.project.quote_IsLead = false;
      this.readOnly = !QuoteService.hasPermission(RolePermission.EditQuote);
    }
    if (value == "convert") {
      this.project.quote_IsLead = false;
      this.isRedirect = true;
    }
  }

  @Output() updated = new EventEmitter<Quote>();

  ngOnInit() {
    this.filteredCustomers = this.customerControl.valueChanges
      .pipe(
        startWith(''),
        map(value =>
          this._filter(value)
        )
      );

    this.filteredCustomers.subscribe(value => {
      if (this.project.quote_ID != null) return;

      let customer = value.find(c => c.customer_BusinessName.toUpperCase() == this.customerControl.value.toUpperCase());

      if (customer != null)
        this.onCustomerChange(value[0]);
      else if (this.customerControl.value != null) {
        this.onCustomerChange(null);
        this.project.customer.customer_BusinessName = this.customerControl.value;
      }
    });

    this.loadCustomers();

    this.quoteService.getCustomerTypes()
      .subscribe({
        next: (data) => {
          this.customerTypes = data;
        }
      });

    this.quoteService.getContactTypes()
      .subscribe({
        next: (data) => {
          this.contactTypes = data;
        }
      });

    this.quoteService.getOffices()
      .subscribe({
        next: (data) => {
          this.offices = data;
        }
      });

    this.quoteService.getSalespersons(true)
      .subscribe({
        next: (data) => {
          this.salesPersons = data;
        }
      });

    this.quoteService.getLeadSourceTypes()
      .subscribe({
        next: (data) => {
          this.leadSourceTypes = data;
        }
      });

    this.quoteService.getJobTypes()
      .subscribe({
        next: (data) => {
          this.jobTypes = data;
        }
      });

    this.quoteService.getStates()
      .subscribe({
        next: (data) => {
          this.states = data;
        }
      });

    this.quoteService.getPaymentTerms()
      .subscribe({
        next: (data) => {
          this.paymentTerms = data;
        }
      });

    this.quoteService.getPaymentTypes()
      .subscribe({
        next: (data) => {
          this.paymentTypes = data;
        }
      });

    this.quoteService.getQuoteTemplates()
      .subscribe({
        next: (data) => {
          this.quoteTemplates = data.filter(d => !d.quote_IsDeleted);
        }
      });
  }

  private loadCustomers() {
    this.quoteService.getCustomers()
      .subscribe({
        next: (data) => {
          this.customers = data;

          this.refreshCustomer();
        }
      });
  }

  private _filter(value: string): Customer[] {
    if (this.customers == null) return new Array();

    const filterValue = value.toLowerCase();
    return this.customers.filter(option => option.customer_BusinessName.toLowerCase().indexOf(filterValue) === 0);
  }

  loadQuote(quote: Quote) {
    this.project = quote;
    this.checkReadOnly();


    if (quote.quote_Converted != null) this.readOnly = true;

    this.refreshCustomer();

    this.blueprintPlans = (quote.quote_Plans != null) ? quote.quote_Plans.toString() : 'false';
    this.fieldMeasure = (quote.quote_Measure != null) ? quote.quote_Measure.toString() : 'false';
  }

  checkReadOnly() {
    if (this.project.quote_IsLead) {
      var isNewLead = (this.project.quote_ID === 0 || this.project.quote_ID === null || this.project.quote_ID === undefined);
      this.readOnly = !QuoteService.hasPermission(RolePermission.EditLead) && (!isNewLead && QuoteService.hasPermission(RolePermission.AddLead));
    }

    //this is a quote
    if (!this.project.quote_IsLead) {
      var isNewQuote = (this.project.quote_ID === 0 || this.project.quote_ID === null || this.project.quote_ID === undefined);
      this.readOnly = !QuoteService.hasPermission(RolePermission.EditQuote) && (!isNewQuote && QuoteService.hasPermission(RolePermission.AddQuote));
    }
  }

  refreshCustomer() {
    if (this.customers != null) {
      let customer = this.customers.find(c => c.customer_ID == this.project.customer_ID);
      // check inactive customer
      if (customer == null && this.project.customer != null && this.project.customer_ID != null) {
        customer = this.project.customer;
        this.customers.push(customer);
      }

      this.onCustomerChange(customer);
      this.customerControl.setValue(this.project.customer.customer_BusinessName);

      if (customer != null) {
        this.jobAddressCustom = !(this.project.quote_Address1 == customer.customer_Address1 &&
          this.project.quote_Address2 == customer.customer_Address2 &&
          this.project.quote_City == customer.customer_City &&
          this.project.quote_State == customer.customer_State &&
          this.project.quote_Postal == customer.customer_Postal);
      }
    }
  }

  onCustomerChange(customer: Customer) {
    if (this.customer == undefined ||
      customer == undefined ||
      this.customer.customer_ID != customer.customer_ID) {
      this.customer = customer;
    }

    if (customer == null) {
      this.project.customer = {
        customer_BusinessName: ''
      } as ProjectCustomerEdit;
      this.project.customer_ID = null;
      //this.project.contact_ID = null;
      this.project.customer.customer_ID = null;
      if (this.offices != null && this.project.quote_ID == null) this.project.office_ID = parseInt(this.offices.find(o => o.office_IsPrimary)?.office_ID);
    } else {
      this.project.customer = (<any>Object).assign({}, customer) as ProjectCustomerEdit;
      if (!this.project.contact_ID || customer.contacts.find(c => c.contact_ID == this.project.contact_ID) == null) this.project.contact_ID = this.project.customer.contact_ID;

      this.onContactChange();

      if (this.project.quote_ID == null) {
        if (customer.customer_AssignedTo)
          this.project.assignmentEdit = [customer.customer_AssignedTo];

        this.project.office_ID = customer.office_ID;

        this.project.quote_PaymentTerm_ID = customer.paymentTerm_ID;
        this.project.quote_PaymentType_ID = customer.paymentType_ID;
      }
    }
  }

  onContactChange() {
    this.project.customer.contact_ID = this.project.contact_ID;

    let contact = this.project.customer.contacts.find(co => co.contact_ID == this.project.contact_ID);
    if (contact == null) {
      this.project.contact_ID = this.project.customer.contact_ID;
      contact = this.project.customer.contacts.find(co => co.contact_ID == this.project.contact_ID);
    }

    if (contact == null) return;

    this.project.customer.contact_FirstName = contact.contact_FirstName;
    this.project.customer.contact_LastName = contact.contact_LastName;
    this.project.customer.contact_Phone = contact.contact_Phone;
    this.project.customer.contact_Mobile = contact.contact_Mobile;
    this.project.customer.contact_Mobile = contact.contact_Mobile;
    this.project.customer.contact_Email = contact.contact_Email;
    this.project.customer.contactType_ID = contact.contactType_ID;
  }

  onJobAddressCustomChanged($event) {
    this.jobAddressCustom = $event.target.checked;

    this.setProjectAddress();
  }

  setProjectAddress() {
    if (this.jobAddressCustom) return;

    this.project.quote_Address1 = this.project.customer.customer_Address1;
    this.project.quote_Address2 = this.project.customer.customer_Address2;
    this.project.quote_City = this.project.customer.customer_City;
    this.project.quote_State = this.project.customer.customer_State;
    this.project.quote_Postal = this.project.customer.customer_Postal;
  }

  cancelEdit() {
    var proceed = confirm($localize`Are you sure?`);
    if (!proceed) return;

    // add lead / quote?  go back to list
    if (this.project.quote_IsLead || this.projectTypeIn == 'convert') {
      this.router.navigate(['/lead/']);
      return;
    }
    if (!this.project.quote_IsLead) {
      this.router.navigate(['/quote/']);
      return;
    }

    this.isRequesting = true;
    this.quoteService.getQuote(this.project.quote_ID)
      .pipe(finalize(() => this.isRequesting = false))
      .subscribe({
        next: (result) => {
          this.quote = result.quote;
        },
        error: (error: string) => this.errors = error
      });
  }

  saveProject(form: NgForm) {
    this.errors = '';

    Object.keys(form.controls).forEach(key => {
      const controlErrors: ValidationErrors = form.controls[key].errors;
      if (controlErrors != null) {
        Object.keys(controlErrors).forEach(keyError => {
          console.log('Key control: ' + key + ', keyError: ' + keyError + ', err value: ', controlErrors[keyError]);
        });
      }
    });

    if (!this.validateContactPhone(form))
      return;

    if (form.valid) {
      this.project.quote_Plans = (this.blueprintPlans == "true");
      this.project.quote_Measure = (this.fieldMeasure == "true");

      this.setProjectAddress();

      if (this.project.quote_ID == null) this.isRedirect = true;

      this.quoteService.saveQuote(this.project)
        .pipe(finalize(() => this.isRequesting = false))
        .subscribe(
          {
            next: (result) => {
              if (this.isRedirect && result) {
                let target = this.project.quote_IsLead ? '/lead/' : '/quote/';
                this.router.navigate([target + result]);
              } else {
                // Update screen to reflect possible customer changes
                this.customers = null;
                this.loadCustomers();
                this.updated.emit(this.project);
                let updatedMessage = this.project.quote_IsLead ? $localize`Lead Info Updated` : $localize`Quote Info Updated`;
                this.snackBar.open(updatedMessage, null, { duration: 2000, verticalPosition: 'bottom', panelClass: ['bidit-snackbar'] });
              }
            },
            error: (error: string) => this.errors = error
          });
    }
  }

  validateContactPhone(form: NgForm): boolean {
    if ((this.project.customer.contact_Mobile == null || this.project.customer.contact_Mobile == '') &&
      (this.project.customer.contact_Phone == null || this.project.customer.contact_Phone == '') &&
      (this.project.customer.contact_Email == null || this.project.customer.contact_Email == '')) {

      form.controls['contact_Mobile'].setErrors({ 'incorrect': true });
      return false;
    }


    form.controls['contact_Mobile'].setErrors(null);
    return true;
  }
}
